import { useEffect, useState } from "react";
import {
  loadColumnVisibility,
  saveColumnVisibility,
  pruneColumnVisibility,
  mergeCustomColumns,
} from "../utils/customFieldUtils";

const loadOrder = (storageKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(`${storageKey}:order`) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Keys the user has placed keep their spot; anything new (a fresh custom field) goes to the end.
const applyOrder = (columns, order) => {
  if (order.length === 0) return columns;
  const rank = (key) => {
    const i = order.indexOf(key);
    return i === -1 ? Infinity : i;
  };
  return [...columns].sort((a, b) => rank(a.key) - rank(b.key));
};

// Saved visibility and order without custom fields; also read when exporting a table that isn't on screen.
export const savedColumns = (storageKey, baseColumns) =>
  applyOrder(mergeCustomColumns(baseColumns, [], loadColumnVisibility(storageKey)), loadOrder(storageKey));

// A stable default: a fresh [] each render would re-run the effect below forever.
const NO_FIELDS = [];

/**
 * Column state for a DataTable: which columns show and in what order, remembered per storageKey.
 * Custom field columns merge in once their definitions arrive.
 */
export default function useTableColumns(storageKey, baseColumns, customFields = NO_FIELDS) {
  const [columns, setColumns] = useState(() => savedColumns(storageKey, baseColumns));

  useEffect(() => {
    if (customFields.length > 0) pruneColumnVisibility(storageKey, customFields);
    setColumns((prev) => applyOrder(
      mergeCustomColumns(prev.length > 0 ? prev : baseColumns, customFields, loadColumnVisibility(storageKey)),
      loadOrder(storageKey)
    ));
    // baseColumns is a module constant at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, storageKey]);

  useEffect(() => {
    saveColumnVisibility(storageKey, columns);
    try {
      localStorage.setItem(`${storageKey}:order`, JSON.stringify(columns.map((c) => c.key)));
    } catch {
      // Storage unavailable — order just doesn't persist.
    }
  }, [storageKey, columns]);

  const toggleColumn = (key) =>
    setColumns((prev) => prev.map((col) => (col.key === key ? { ...col, visible: !col.visible } : col)));

  const moveColumn = (fromKey, toKey) =>
    setColumns((prev) => {
      const from = prev.findIndex((c) => c.key === fromKey);
      const to = prev.findIndex((c) => c.key === toKey);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });

  return { columns, toggleColumn, moveColumn };
}
